import type {
  CopilotProposal,
  CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import type { Chapter } from "@repo/app-types/entities/course";
import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import type {
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { TrainingOperationError } from "@repo/app-types/entities/trainingProposal";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { Input } from "@repo/ui/Inputs/input";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useState, type FC } from "react";
import { Tree, TreeItem, TreeItemContent } from "react-aria-components";
import { Link } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useResolveProposalMutation } from "../../../../api/entities/copilotProposal.js";
import { isConflictQueryError } from "../../../../api/queryErrors.js";
import { ResourceSaveFooter } from "../../../../components/resourceSaveFooter.js";
import {
  addActionItemContent,
  treeItem,
} from "../../../../components/tree/treeStyles.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { useUnsavedChangesGuard } from "../../../../hooks/useUnsavedChangesGuard.js";
import { ChapterItemSettingsContent } from "../../../courses/chapterItemSettingsContent.js";
import { ChapterSettings } from "../../../courses/chapterSettings.js";
import { ChapterTreeItem } from "../../../courses/chapterTreeItem.js";
import { LessonItemSettingsContent } from "../../../courses/lessonItemSettingsContent.js";
import { LessonTreeItem } from "../../../courses/lessonTreeItem.js";
import { ChatQuizInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/chatQuiz/settings.js";
import { useInterviewSectionCreateFlow } from "../../../interviewSections/definitions/createFlow.js";
import { KnowledgeTestInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/knowledgeTest/settings.js";
import { RolePlayInterviewSectionDefinitionSettings } from "../../../interviewSections/definitions/rolePlay/settings.js";
import type { VisibleInterviewSectionDefinition } from "../../../interviewSections/definitions/state.js";
import { getInterviewSectionTypeDisplayValues } from "../../../interviewSections/types.js";
import { useDeepLinkedProposal } from "../../../researchAgent/copilotDeepLinks.js";
import { useReportEditorProposal } from "../../../researchAgent/editorProposalsSlice.js";
import {
  serializeTreeItemKey,
  useCourseTreeExpandAndCollapse,
} from "./courseTreeHelpers.js";
import {
  renderSectionPreviewTrigger,
  useCourseTreeDragAndDrop,
  useCourseTreeSelection,
} from "./courseTreeInteractions.js";
import { useLearningJourneyEditor } from "./useLearningJourneyEditor.js";

interface LearningJourneyBuilderPageProps {
  project: ProjectWithInterviewCount & ProjectWithJobType;
}

export const LearningJourneyBuilderPage: FC<
  LearningJourneyBuilderPageProps
> = ({ project }) => {
  const editor = useLearningJourneyEditor(project);
  const {
    course,
    sections,
    sectionsById,
    playbookId,
    chapterChangeStatuses,
    lessonChangeStatuses,
    hasMadeChanges,
    isLoadingSections,
    isSaving,
    onCourseChange,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onProposalReplay,
    onDiscardChanges,
    onSaveChanges,
  } = editor;

  const [resolveProposal] = useResolveProposalMutation();
  const [linkedProposalId, setLinkedProposalId] = useState<
    CopilotProposalId | undefined
  >(undefined);
  const [proposalError, setProposalError] = useState<string | null>(null);

  const { expandedKeys, onExpandedChange } =
    useCourseTreeExpandAndCollapse(course);
  const { dragAndDropHooks } = useCourseTreeDragAndDrop(course, onCourseChange);
  const {
    selectedKeys,
    onSelectionChange,
    selectedChapter,
    selectedChapterNumber,
    selectedLessonNumber,
    selectedSection,
    setSelectedKey,
  } = useCourseTreeSelection({
    initialSelectedKey: undefined,
    course,
    sectionsById,
  });

  // One-shot link from an Ask Alpharun proposal card (?proposalId=...): the
  // proposal's operations replay onto the saved training and seed the draft as
  // soon as the sections are loaded. A proposal for another training replays
  // nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("training", {
      onUnavailable: () =>
        setProposalError("This suggestion is no longer available."),
    });
  const putProposalIntoDraft = useEffectEvent(
    (proposal: CopilotProposal<"training">) => {
      try {
        setSelectedKey(onProposalReplay(proposal.data.operations));
      } catch (error) {
        if (!(error instanceof TrainingOperationError)) {
          throw error;
        }
        setProposalError("This suggestion no longer applies.");
        return;
      }
      setLinkedProposalId(proposal.id);
    },
  );
  useEffect(() => {
    if (isLoadingSections || !deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.resource_id !== project.id) {
      return;
    }
    putProposalIntoDraft(deepLinkProposal);
  }, [
    isLoadingSections,
    deepLinkProposal,
    clearDeepLinkProposalId,
    project.id,
    putProposalIntoDraft,
  ]);

  useReportEditorProposal(linkedProposalId, isSaving);
  const { guardModal } = useUnsavedChangesGuard(hasMadeChanges);

  // Dropping the draft drops the suggestion with it: the row stays pending, so
  // its card goes back to offering a review.
  const handleDiscard = () => {
    onDiscardChanges();
    setLinkedProposalId(undefined);
    setProposalError(null);
  };

  const onChapterCreateClick = () => {
    const newChapter: Chapter = {
      id: `client:${uuidv4()}`,
      lessons: [],
    };

    onCourseChange({
      ...course,
      chapters: [...course.chapters, newChapter],
    });
    setSelectedKey({ type: "chapter", id: newChapter.id });
  };

  const [lessonCreateRequest, setLessonCreateRequest] = useState<
    { chapterIndex: number } | undefined
  >(undefined);

  const onLessonCreateFinish = (settings: InterviewSectionSettings) => {
    if (!lessonCreateRequest) {
      return;
    }

    const newSectionId = onSectionCreate(settings);

    onCourseChange({
      ...course,
      chapters: course.chapters.map((chapter, index) => {
        if (index !== lessonCreateRequest.chapterIndex) {
          return chapter;
        }

        return {
          ...chapter,
          lessons: [
            ...chapter.lessons,
            { interviewSectionDefinitionId: newSectionId },
          ],
        };
      }),
    });

    setSelectedKey({ type: "lesson", sectionId: newSectionId });
  };

  const { onInterviewSectionCreateClick, renderInterviewSectionCreateFlow } =
    useInterviewSectionCreateFlow({
      isTrainingProject: true,
      title: "Add lesson",
      description: "Choose the type of lesson to add to your learning journey.",
      onSectionCreate: onLessonCreateFinish,
    });

  const onLessonCreateClick = (chapterIndex: number) => {
    setLessonCreateRequest({ chapterIndex });
    onInterviewSectionCreateClick();
  };

  const onDeleteClick = () => {
    if (selectedSection) {
      onSectionDelete(selectedSection.id);
      onCourseChange({
        ...course,
        chapters: course.chapters.map((chapter) => ({
          ...chapter,
          lessons: chapter.lessons.filter(
            (lesson) =>
              lesson.interviewSectionDefinitionId !== selectedSection.id,
          ),
        })),
      });
      setSelectedKey(undefined);
      return;
    }

    if (!selectedChapter) {
      return;
    }

    for (const lesson of selectedChapter.lessons) {
      onSectionDelete(lesson.interviewSectionDefinitionId);
    }

    onCourseChange({
      ...course,
      chapters: course.chapters.filter(
        (chapter) => chapter.id !== selectedChapter.id,
      ),
    });
    setSelectedKey(undefined);
  };

  const canConfigure = Boolean(playbookId);

  const handleSave = async () => {
    try {
      await onSaveChanges();
    } catch (error) {
      console.error("Failed to save learning journey", error);
      return;
    }
    if (!linkedProposalId) {
      return;
    }
    // The saved draft carried the suggestion, so it is applied. One that has
    // moved on (409) was already decided; the save stands either way. Any other
    // failure keeps the suggestion linked, so the next Save retries it.
    try {
      await resolveProposal({
        proposalId: linkedProposalId,
        status: "applied",
      }).unwrap();
    } catch (error) {
      if (!isConflictQueryError(error)) {
        setProposalError(
          "The training was saved, but the suggestion could not be marked as applied.",
        );
        return;
      }
      setProposalError("This suggestion is no longer available.");
    }
    setLinkedProposalId(undefined);
  };

  const treeStyle = `
    @scope {
      .react-aria-DropIndicator {
        outline: 1px solid #9333ea;
      }
    }
  `;

  return (
    <div className="flex h-full min-w-[1100px] flex-col">
      {guardModal}

      {proposalError ? (
        <div className="border-b border-border-light bg-background px-5 py-3">
          <p className="text-sm text-red-600">{proposalError}</p>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
          {!canConfigure ? (
            <div className="flex h-full items-center justify-center px-5">
              <div className="max-w-md text-center">
                <h2 className="text-sm font-semibold text-foreground">
                  Select a playbook in the Overview tab
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configuration uses the attached playbook and its knowledge
                  base to shape the learning journey.
                </p>
                <div className="mt-4">
                  <Link
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    to={buildProjectUrlPath(
                      project.id,
                      ProjectDetailsTabPath.Overview,
                    )}
                  >
                    Go to Overview
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid h-full min-h-0 grid-cols-[400px_minmax(0,1fr)]">
              <div className="flex min-h-0 flex-col border-r border-border-light">
                <div className="flex items-center justify-between border-b border-border-light px-4 py-4">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      Chapters and lessons
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Drag to reorder chapters and lessons.
                    </p>
                  </div>
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={onChapterCreateClick}
                  >
                    Add chapter
                  </Button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                  <style hidden>{treeStyle}</style>
                  {isLoadingSections ? (
                    <div className="text-sm text-muted-foreground">
                      Loading lessons...
                    </div>
                  ) : (
                    <Tree
                      aria-label="Learning journey"
                      selectionMode="single"
                      selectionBehavior="replace"
                      selectedKeys={selectedKeys}
                      expandedKeys={expandedKeys}
                      onExpandedChange={onExpandedChange}
                      onSelectionChange={onSelectionChange}
                      dragAndDropHooks={dragAndDropHooks}
                      disabledBehavior="selection"
                    >
                      {course.chapters.map((chapter, chapterIndex) => (
                        <ChapterTreeItem
                          key={chapter.id}
                          chapter={chapter}
                          index={chapterIndex}
                        >
                          <TreeItemContent>
                            <ChapterItemSettingsContent
                              index={chapterIndex}
                              chapter={chapter}
                              changeStatus={chapterChangeStatuses.get(
                                chapter.id,
                              )}
                            />
                          </TreeItemContent>

                          {chapter.lessons.map((lesson) => {
                            const section = sectionsById?.get(
                              lesson.interviewSectionDefinitionId,
                            );

                            return (
                              <LessonTreeItem
                                key={lesson.interviewSectionDefinitionId}
                                sectionId={lesson.interviewSectionDefinitionId}
                                section={section}
                              >
                                <TreeItemContent>
                                  <LessonItemSettingsContent
                                    section={section}
                                    changeStatus={lessonChangeStatuses.get(
                                      lesson.interviewSectionDefinitionId,
                                    )}
                                  />
                                </TreeItemContent>
                              </LessonTreeItem>
                            );
                          })}

                          <TreeItem
                            key={`new-${chapter.id}`}
                            id={serializeTreeItemKey({
                              type: "new-lesson",
                              chapterId: chapter.id,
                            })}
                            textValue="Add lesson"
                            className={treeItem({
                              isLastItemAtLevel: true,
                            })}
                            onAction={() => onLessonCreateClick(chapterIndex)}
                            isDisabled
                          >
                            <TreeItemContent>
                              <div className={addActionItemContent()}>
                                <IconPlus className="size-4" />
                                Add lesson
                              </div>
                            </TreeItemContent>
                          </TreeItem>
                        </ChapterTreeItem>
                      ))}
                    </Tree>
                  )}

                  {renderInterviewSectionCreateFlow()}
                </div>
              </div>

              <div className="flex min-h-0 flex-col">
                <div className="flex items-center justify-between border-b border-border-light px-4 py-4">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      {selectedSection
                        ? `Lesson ${selectedLessonNumber}: ${
                            getInterviewSectionTypeDisplayValues(
                              selectedSection.settings.type,
                            ).title
                          }`
                        : selectedChapter
                          ? `Chapter ${selectedChapterNumber}`
                          : "Select a chapter or lesson"}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {selectedSection
                        ? "Edit lesson content and instructions."
                        : selectedChapter
                          ? "Refine the chapter focus and grading context."
                          : "Choose an item from the left to edit it here."}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectedSection
                      ? renderSectionPreviewTrigger(project.id, selectedSection)
                      : null}

                    {selectedSection || selectedChapter ? (
                      <Button
                        variant={ButtonVariant.Warning}
                        icon={<IconTrash className="size-4" />}
                        onClick={onDeleteClick}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                  {selectedSection ? (
                    <>
                      <div className="border-b border-gray-100 px-4 py-3 text-sm text-muted-foreground">
                        <div className="max-w-[760px]">
                          {
                            getInterviewSectionTypeDisplayValues(
                              selectedSection.settings.type,
                            ).description
                          }
                        </div>
                      </div>
                      <div className="w-full">
                        {renderSectionSettings({
                          section: selectedSection,
                          onSectionUpdate,
                        })}
                      </div>
                    </>
                  ) : null}

                  {selectedChapter ? (
                    <div className="max-w-[760px]">
                      <ChapterSettings
                        chapter={selectedChapter}
                        playbookId={playbookId}
                        onChange={(chapterId, patchChapter) => {
                          onCourseChange({
                            ...course,
                            chapters: course.chapters.map((existingChapter) => {
                              if (existingChapter.id === chapterId) {
                                return patchChapter(existingChapter);
                              }
                              return existingChapter;
                            }),
                          });
                        }}
                      />
                    </div>
                  ) : null}

                  {!selectedSection && !selectedChapter ? (
                    <div className="flex h-full min-h-[240px] items-center justify-center px-5 text-center text-sm text-muted-foreground">
                      Select a chapter or lesson from the left to edit it.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {hasMadeChanges || linkedProposalId ? (
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.JOB_OPENINGS}
          passThrough={true}
        >
          {(allowed) => (
            <ResourceSaveFooter
              onCancel={handleDiscard}
              onSave={() => void handleSave()}
              isSaving={isSaving}
              isSaveDisabled={!allowed || !canConfigure}
            />
          )}
        </Can>
      ) : null}
    </div>
  );
};

function renderSectionSettings(args: {
  section: VisibleInterviewSectionDefinition;
  onSectionUpdate: (id: string, settings: InterviewSectionSettings) => void;
}) {
  const { section, onSectionUpdate } = args;
  const settings = section.settings;

  switch (settings.type) {
    case InterviewSectionType.KnowledgeTest:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              value={settings.title ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  title: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <KnowledgeTestInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.RolePlay:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              description="This will be visible to participants to help them understand what they will learn."
              placeholder="Example: Handling price objections"
              value={settings.title ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  title: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <RolePlayInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.ChatQuiz:
      return (
        <div className="space-y-4">
          <div className="px-4 pt-4 pb-0">
            <Input
              label="Topic"
              description="This will be visible to participants to help them understand what they will learn."
              placeholder="Example: Pricing and discounts"
              value={settings.topic ?? ""}
              onChange={(event) => {
                onSectionUpdate(section.id, {
                  ...settings,
                  topic: event.target.value,
                });
              }}
              maxLength={100}
            />
          </div>

          <ChatQuizInterviewSectionDefinitionSettings
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={settings}
            onChange={onSectionUpdate}
          />
        </div>
      );
    case InterviewSectionType.Questions:
      return (
        <div className="p-4 text-sm text-muted-foreground">
          This legacy lesson type is not supported in the Journey Builder.
        </div>
      );
    default:
      return assertUnreachable(settings, () => null);
  }
}
